import { Order } from '@libs-classes/order.class';
import { OrderStatusEnum } from '@libs-enums/status.enum';
import { IOrderDataUpdate } from '@lib-typing/order.interface';
import { BoardDropSideType, IBoardDropInput, IBoardDropPlan, IBoardOrderPatch, IBoardOrderUpdate } from './board-drop.interface';

/**
 * Планировщик переноса заявок на доске: по снимку доски в момент отпускания решает,
 * какие заявки куда встают и какие поля у каких заявок меняются. Чистые функции — без Angular и без мутаций.
 */

/** id списка «Нераспределённые» в шаблоне доски. */
export const UNASSIGNED_LIST_ID = 'newOrdersList';

/** Префикс id ленты работника в шаблоне доски: `worker-<id>`. */
export const WORKER_LIST_PREFIX = 'worker-';

/** id работника по id списка CDK; null — список «Нераспределённые». */
export function parseBoardListId(listId: string): number | null {
    return listId.startsWith(WORKER_LIST_PREFIX) ? Number(listId.slice(WORKER_LIST_PREFIX.length)) : null;
}

/**
 * Порядок карточек в ленте, как их видит пользователь: по часу начала слота, внутри часа — по номеру очереди.
 * Заявка без номера стоит первой в своём часе — так её рисует доска (`queueNumber || 0`),
 * поэтому и перенумеровывать ленту нужно в этом же порядке, иначе такая заявка прыгнет.
 */
export function compareBoardOrder(a: Order, b: Order): number {
    return slotHour(a) - slotHour(b) || (a.data.queueNumber || 0) - (b.data.queueNumber || 0);
}

/**
 * Решает, что произойдёт при отпускании заявки.
 * Возвращает null, если перенос не принимается или ничего на доске не меняет — тогда на сервер ничего не отправляется.
 */
export function planBoardDrop(input: IBoardDropInput): IBoardDropPlan | null {
    if (!isDropAccepted(input)) {
        return null;
    }

    const moved = pickMovedOrders(input);
    const isSameList = input.sourceWorkerId === input.targetWorkerId;

    let plan: IBoardDropPlan | null;
    if (input.targetWorkerId === null) {
        // Внутри «Нераспределённых» порядок задаёт сортировка списка, сохранять нечего
        plan = isSameList ? null : planUnassign(input, moved);
    } else {
        plan = planAssign(input, input.targetWorkerId, moved, isSameList);
    }

    return plan && plan.updates.length ? plan : null;
}

/** Батч для `PUT /orders/batch-update`: id заявки и только изменённые поля, даты — в поясе для сохранения. */
export function toBatchPayload(updates: IBoardOrderUpdate[], toServerDate: (date: Date) => Date): IOrderDataUpdate[] {
    return updates.map(({ order, patch }) => {
        const { dateFrom, dateTo, ...fields } = patch;
        return {
            id: order.data.id,
            ...fields,
            ...(dateFrom ? { dateFrom: toServerDate(dateFrom) } : {}),
            ...(dateTo ? { dateTo: toServerDate(dateTo) } : {}),
        };
    });
}

/**
 * Правила приёма — те же, что были на доске всегда: без выделения перенос возможен, только когда курсор над доской,
 * а перестановка внутри одного списка — только через индикатор слева или справа от карточки.
 * Перенос выделенных заявок эти ограничения не проверяет.
 */
function isDropAccepted(input: IBoardDropInput): boolean {
    if (input.selectedOrders.length) {
        return true;
    }

    if (!input.pointerOverBoard) {
        return false;
    }

    return input.sourceWorkerId !== input.targetWorkerId || input.side !== '';
}

/**
 * Какие заявки едут: выделенные заявки исходного списка, а если в нём ничего не выделено — только перетаскиваемая.
 * Выделение в других списках не переносится — так же их считает превью перетаскивания («Заявок N»).
 */
function pickMovedOrders(input: IBoardDropInput): Order[] {
    const selectedInSource = input.sourceOrders.filter(order => input.selectedOrders.includes(order));
    return sortBoardOrder(selectedInSource.length ? selectedInSource : [input.dragged]);
}

/** Перенос в ленту работника: вставка к карточке под курсором или в конец своего часа, затем перенумерация. */
function planAssign(input: IBoardDropInput, workerId: number, moved: Order[], isSameList: boolean): IBoardDropPlan | null {
    const rest = sortBoardOrder(input.targetOrders.filter(order => !moved.includes(order)));
    // Карточка под курсором годится как ориентир, только если она в этой ленте и сама не переносится
    const anchor = rest.find(order => order.data.id === input.hoveredOrderId) ?? null;

    // Внутри одной ленты место задаёт только карточка-ориентир; отпустили на себя или мимо — ничего не меняется
    if (isSameList && !anchor) {
        return null;
    }

    const targetOrders = anchor ? insertNextTo(rest, anchor, moved, input.side) : insertAtEndOfSlotHours(rest, moved);
    const updates = collectUpdates(targetOrders.map((order, index) => {
        if (!moved.includes(order)) {
            return { order, next: { queueNumber: index + 1 } };
        }

        // Перенесённая к карточке заявка забирает её слот; на свободное место — сохраняет свой
        return {
            order,
            next: {
                executorId: workerId,
                status: order.data.status === OrderStatusEnum.active ? OrderStatusEnum.destributed : order.data.status,
                queueNumber: index + 1,
                ...(anchor ? { dateFrom: new Date(anchor.data.dateFrom), dateTo: new Date(anchor.data.dateTo) } : {}),
            },
        };
    }));

    const sourceOrders = isSameList || input.sourceWorkerId === null ? null : withoutOrders(input.sourceOrders, moved);
    updates.push(...renumber(sourceOrders ?? []));

    return { movedOrders: moved, targetOrders, sourceOrders, updates };
}

/** Перенос в «Нераспределённые»: заявки теряют исполнителя и становятся активными, слот и номер остаются. */
function planUnassign(input: IBoardDropInput, moved: Order[]): IBoardDropPlan {
    const updates = collectUpdates(moved.map(order => ({
        order,
        next: { executorId: null, status: OrderStatusEnum.active },
    })));

    const sourceOrders = input.sourceWorkerId === null ? null : withoutOrders(input.sourceOrders, moved);
    updates.push(...renumber(sourceOrders ?? []));

    return { movedOrders: moved, targetOrders: null, sourceOrders, updates };
}

/** Вставка группы рядом с карточкой-ориентиром: слева — перед ней, иначе — сразу после. */
function insertNextTo(rest: Order[], anchor: Order, moved: Order[], side: BoardDropSideType): Order[] {
    const index = rest.indexOf(anchor) + (side === 'left' ? 0 : 1);
    return [...rest.slice(0, index), ...moved, ...rest.slice(index)];
}

/** Вставка на свободное место: каждая заявка встаёт последней среди заявок своего часа. */
function insertAtEndOfSlotHours(rest: Order[], moved: Order[]): Order[] {
    const result = [...rest];
    moved.forEach(order => {
        const laterIndex = result.findIndex(other => slotHour(other) > slotHour(order));
        result.splice(laterIndex === -1 ? result.length : laterIndex, 0, order);
    });
    return result;
}

/** Номера очереди 1..N по порядку ленты — только для заявок, у которых номер меняется. */
function renumber(orders: Order[]): IBoardOrderUpdate[] {
    return collectUpdates(orders.map((order, index) => ({ order, next: { queueNumber: index + 1 } })));
}

/** Лента без перенесённых заявок, в порядке доски. */
function withoutOrders(orders: Order[], moved: Order[]): Order[] {
    return sortBoardOrder(orders.filter(order => !moved.includes(order)));
}

/** Оставляет только заявки, у которых действительно что-то меняется, и только изменённые поля. */
function collectUpdates(candidates: { order: Order; next: IBoardOrderPatch }[]): IBoardOrderUpdate[] {
    return candidates
        .map(({ order, next }) => ({ order, patch: diffOrder(order, next) }))
        .filter(update => Object.keys(update.patch).length > 0);
}

/** Поля из `next`, значение которых отличается от текущего в заявке. */
function diffOrder(order: Order, next: IBoardOrderPatch): IBoardOrderPatch {
    const patch: IBoardOrderPatch = {};

    if (next.executorId !== undefined && next.executorId !== (order.data.executor?.id ?? null)) {
        patch.executorId = next.executorId;
    }

    if (next.status !== undefined && next.status !== order.data.status) {
        patch.status = next.status;
    }

    if (next.queueNumber !== undefined && next.queueNumber !== order.data.queueNumber) {
        patch.queueNumber = next.queueNumber;
    }

    if (next.dateFrom && next.dateFrom.getTime() !== new Date(order.data.dateFrom).getTime()) {
        patch.dateFrom = next.dateFrom;
    }

    if (next.dateTo && next.dateTo.getTime() !== new Date(order.data.dateTo).getTime()) {
        patch.dateTo = next.dateTo;
    }

    return patch;
}

/** Копия списка в порядке доски; сортировка устойчивая, поэтому равные сохраняют исходный порядок. */
function sortBoardOrder(orders: Order[]): Order[] {
    return [...orders].sort(compareBoardOrder);
}

/** Час начала слота — по нему доска раскладывает карточки по колонкам времени. */
function slotHour(order: Order): number {
    return new Date(order.data.dateFrom).getHours();
}
